// src/services/sirvoyArchives.js
import { sirvoyDb } from './firebaseConfig';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';

/**
 * Convertit une date string "YYYY-MM-DD" en objet Date
 */
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  if (dateStr.toDate) return dateStr.toDate(); // Timestamp Firebase
  return new Date(dateStr);
};

/**
 * Normalise une date pour comparaison (début de journée)
 */
const normalizeDate = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Récupère les réservations Sirvoy pour une période donnée (pour le calendrier)
 * Ne retourne que les réservations NON annulées
 * 
 * @param {Date} startDate - Début de la période
 * @param {Date} endDate - Fin de la période
 * @returns {Promise<Array>} Liste des réservations formatées pour le calendrier
 */
export async function getSirvoyReservationsForPeriod(startDate, endDate) {
  try {
    const bookingsRef = collection(sirvoyDb, 'bookings');
    const snapshot = await getDocs(bookingsRef);
    
    const normalizedStart = normalizeDate(startDate);
    const normalizedEnd = normalizeDate(endDate);
    
    const reservations = [];
    
    snapshot.forEach(docSnap => {
      const data = docSnap.data();
      
      // Ignorer les annulées
      if (data.cancelled) return;
      
      const checkIn = parseDate(data.checkIn);
      const checkOut = parseDate(data.checkOut);
      
      if (!checkIn || !checkOut) return;
      
      // Vérifier si la réservation chevauche la période
      const normalizedCheckIn = normalizeDate(checkIn);
      const normalizedCheckOut = normalizeDate(checkOut);
      
      // Chevauchement : checkIn < endPeriod ET checkOut > startPeriod
      if (normalizedCheckIn <= normalizedEnd && normalizedCheckOut >= normalizedStart) {
        // Traiter chaque chambre de la réservation
        if (data.rooms && Array.isArray(data.rooms) && data.rooms.length > 0) {
          data.rooms.forEach((room, index) => {
            // Ignorer les chambres GHOST (annulations)
            if (room.roomNo?.includes('GHOST') || room.specification === 'OFF') return;
            
            reservations.push({
              id: `sirvoy-${data.bookingNo}-room-${index}`,
              reservationId: `sirvoy-${data.bookingNo}`,
              bookingNo: data.bookingNo,
              number: data.bookingNo,
              
              // Dates
              checkIn: checkIn,
              checkOut: checkOut,
              createdAt: data.bookingDate?.toDate ? data.bookingDate.toDate() : parseDate(data.bookingDateRaw),
              
              // Chambre
              roomNumber: room.roomNo || '',
              roomType: room.specification || '',
              roomId: null, // Sera mappé par CalendarView via roomNumber
              
              // Client
              guestName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Client',
              firstName: data.firstName || '',
              lastName: data.lastName || '',
              email: data.email || '',
              phone: data.phone || '',
              
              // Infos
              guests: room.guests || data.numberOfGuests || 2,
              totalGuests: data.numberOfGuests || 2,
              price: data.total || 0,
              
              // Status
              status: data.checkedOut ? 'checked-out' : data.checkedIn ? 'checked-in' : 'confirmed',
              
              // Marqueurs archives
              isArchive: true,
              source: 'sirvoy',
              
              // Index pour multi-chambres
              roomIndex: index,
              totalRooms: data.rooms.length
            });
          });
        } else {
          // Réservation sans détail de chambre (rare)
          reservations.push({
            id: `sirvoy-${data.bookingNo}`,
            reservationId: `sirvoy-${data.bookingNo}`,
            bookingNo: data.bookingNo,
            number: data.bookingNo,
            
            checkIn: checkIn,
            checkOut: checkOut,
            createdAt: data.bookingDate?.toDate ? data.bookingDate.toDate() : parseDate(data.bookingDateRaw),
            
            roomNumber: '',
            roomType: '',
            roomId: null,
            
            guestName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Client',
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            phone: data.phone || '',
            
            guests: data.numberOfGuests || 2,
            totalGuests: data.numberOfGuests || 2,
            price: data.total || 0,
            
            status: data.checkedOut ? 'checked-out' : data.checkedIn ? 'checked-in' : 'confirmed',
            
            isArchive: true,
            source: 'sirvoy',
            
            roomIndex: 0,
            totalRooms: 1
          });
        }
      }
    });
    
    console.log(`📦 Sirvoy: ${reservations.length} réservations chargées pour la période`);
    return reservations;
    
  } catch (error) {
    console.error('Erreur chargement archives Sirvoy:', error);
    return [];
  }
}

/**
 * Récupère le détail complet d'une réservation Sirvoy
 * 
 * @param {string} bookingNo - Numéro de réservation Sirvoy
 * @returns {Promise<Object|null>} Réservation complète ou null
 */
export async function getSirvoyReservation(bookingNo) {
  try {
    // Le bookingNo peut être préfixé par "sirvoy-"
    const cleanBookingNo = bookingNo.replace('sirvoy-', '').split('-room-')[0];
    
    const docRef = doc(sirvoyDb, 'bookings', cleanBookingNo);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.warn(`Réservation Sirvoy ${cleanBookingNo} non trouvée`);
      return null;
    }
    
    const data = docSnap.data();
    
    // Récupérer les infos client associées
    let clientData = null;
    if (data.clientId) {
      try {
        const clientDoc = await getDoc(doc(sirvoyDb, 'clients', data.clientId));
        if (clientDoc.exists()) {
          clientData = clientDoc.data();
        }
      } catch (e) {
        console.warn('Client Sirvoy non trouvé:', data.clientId);
      }
    }
    
    return {
      id: `sirvoy-${data.bookingNo}`,
      bookingNo: data.bookingNo,
      number: data.bookingNo,
      
      // Dates
      checkIn: parseDate(data.checkIn),
      checkOut: parseDate(data.checkOut),
      createdAt: data.bookingDate?.toDate ? data.bookingDate.toDate() : parseDate(data.bookingDateRaw),
      bookingDate: data.bookingDate?.toDate ? data.bookingDate.toDate() : parseDate(data.bookingDateRaw),
      
      // Client (format compatible VALETIER)
      client: {
        firstName: data.firstName || '',
        name: data.lastName || '',
        email: data.email || '',
        phone: data.phone || '',
        address: clientData?.address || '',
        city: clientData?.city || '',
        postalCode: clientData?.postalCode || '',
        country: clientData?.country || 'FR',
        company: clientData?.company || ''
      },
      clientId: data.clientId,
      
      // Chambres (format compatible VALETIER)
      rooms: (data.rooms || []).map(room => ({
        roomNumber: room.roomNo || '',
        roomType: room.specification || '',
        pricePerNight: room.unitPrice || 0,
        totalPrice: room.total || 0,
        occupancy: room.guests || 2
      })),
      
      // Extras / Consommations
      consumptions: (data.extras || []).map(extra => ({
        description: extra.specification || '',
        date: extra.date || '',
        quantity: extra.units || 1,
        unitPrice: extra.unitPrice || 0,
        totalPrice: extra.total || 0,
        type: categorizeExtra(extra.specification)
      })),
      
      // Paiements
      payments: (data.payments || []).map(payment => ({
        method: payment.method || '',
        date: payment.date || '',
        amount: payment.amount || 0,
        reference: payment.reference || ''
      })),
      
      // Pricing
      pricing: {
        total: data.total || 0,
        paid: data.paid || 0,
        roomTotal: (data.rooms || []).reduce((sum, r) => sum + (r.total || 0), 0)
      },
      
      // Guests
      guests: {
        adults: data.numberOfGuests || 2,
        children: 0,
        total: data.numberOfGuests || 2
      },
      numberOfGuests: data.numberOfGuests || 2,
      
      // Status
      status: data.cancelled ? 'cancelled' : 
              data.checkedOut ? 'checked-out' : 
              data.checkedIn ? 'checked-in' : 
              data.confirmed ? 'confirmed' : 'pending',
      cancelled: data.cancelled || false,
      
      // Notes
      notes: data.guestMessage || '',
      hotelNotes: data.comment || '',
      guestMessage: data.guestMessage || '',
      
      // Source
      source: data.source || 'Site web',
      language: clientData?.language || 'fr',
      
      // Marqueurs archive
      isArchive: true,
      archiveSource: 'sirvoy'
    };
    
  } catch (error) {
    console.error('Erreur récupération réservation Sirvoy:', error);
    return null;
  }
}

/**
 * Catégorise un extra Sirvoy pour l'affichage
 */
function categorizeExtra(specification) {
  if (!specification) return 'other';
  const spec = specification.toLowerCase();
  
  if (spec.includes('restaurant') || spec.includes('restauration')) return 'restaurant';
  if (spec.includes('taxe') || spec.includes('séjour')) return 'taxes';
  if (spec.includes('massage')) return 'spa';
  if (spec.includes('navette') || spec.includes('gare')) return 'transport';
  if (spec.includes('équitation') || spec.includes('equitation')) return 'activity';
  if (spec.includes('honesty') || spec.includes('bar')) return 'bar';
  if (spec.includes('charge') || spec.includes('ve')) return 'parking';
  if (spec.includes('lit')) return 'room';
  
  return 'other';
}

/**
 * Récupère l'historique Sirvoy d'un client par email
 * 
 * @param {string} email - Email du client
 * @returns {Promise<Array>} Liste des réservations du client
 */
export async function getSirvoyClientHistoryByEmail(email) {
  try {
    if (!email) return [];
    
    const normalizedEmail = email.toLowerCase().trim();
    
    // Chercher le client par email
    const clientsRef = collection(sirvoyDb, 'clients');
    const snapshot = await getDocs(clientsRef);
    
    let clientId = null;
    snapshot.forEach(docSnap => {
      const data = docSnap.data();
      if (data.email?.toLowerCase() === normalizedEmail) {
        clientId = docSnap.id;
      }
    });
    
    if (!clientId) {
      console.log(`Aucun client Sirvoy trouvé pour ${email}`);
      return [];
    }
    
    // Récupérer les réservations du client
    const bookingsRef = collection(sirvoyDb, 'bookings');
    const bookingsSnapshot = await getDocs(bookingsRef);
    
    const reservations = [];
    bookingsSnapshot.forEach(docSnap => {
      const data = docSnap.data();
      if (data.clientId === clientId || data.email?.toLowerCase() === normalizedEmail) {
        reservations.push({
          id: `sirvoy-${data.bookingNo}`,
          bookingNo: data.bookingNo,
          number: data.bookingNo,
          checkIn: parseDate(data.checkIn),
          checkOut: parseDate(data.checkOut),
          createdAt: data.bookingDate?.toDate ? data.bookingDate.toDate() : parseDate(data.bookingDateRaw),
          status: data.cancelled ? 'cancelled' : 
                  data.checkedOut ? 'checked-out' : 
                  data.checkedIn ? 'checked-in' : 'confirmed',
          total: data.total || 0,
          rooms: data.rooms || [],
          isArchive: true,
          source: 'sirvoy'
        });
      }
    });
    
    // Trier par date de check-in décroissante
    reservations.sort((a, b) => (b.checkIn || 0) - (a.checkIn || 0));
    
    console.log(`📦 Sirvoy: ${reservations.length} réservations trouvées pour ${email}`);
    return reservations;
    
  } catch (error) {
    console.error('Erreur récupération historique client Sirvoy:', error);
    return [];
  }
}

/**
 * Récupère les infos client Sirvoy par email
 * 
 * @param {string} email - Email du client
 * @returns {Promise<Object|null>} Données client ou null
 */
export async function getSirvoyClientByEmail(email) {
  try {
    if (!email) return null;
    
    const normalizedEmail = email.toLowerCase().trim();
    const clientsRef = collection(sirvoyDb, 'clients');
    const snapshot = await getDocs(clientsRef);
    
    let clientData = null;
    snapshot.forEach(docSnap => {
      const data = docSnap.data();
      if (data.email?.toLowerCase() === normalizedEmail) {
        clientData = {
          id: docSnap.id,
          ...data,
          isArchive: true,
          source: 'sirvoy'
        };
      }
    });
    
    return clientData;
    
  } catch (error) {
    console.error('Erreur recherche client Sirvoy:', error);
    return null;
  }
}

/**
 * Récupère les statistiques globales Sirvoy pour un client
 * 
 * @param {string} email - Email du client
 * @returns {Promise<Object>} Statistiques (nombre de séjours, CA total, etc.)
 */
export async function getSirvoyClientStats(email) {
  try {
    const reservations = await getSirvoyClientHistoryByEmail(email);
    
    const confirmedRes = reservations.filter(r => r.status !== 'cancelled');
    const cancelledRes = reservations.filter(r => r.status === 'cancelled');
    
    const totalSpent = confirmedRes.reduce((sum, r) => sum + (r.total || 0), 0);
    
    const years = [...new Set(confirmedRes.map(r => {
      const d = r.checkIn;
      return d ? d.getFullYear() : null;
    }).filter(Boolean))].sort();
    
    return {
      totalReservations: confirmedRes.length,
      totalCancellations: cancelledRes.length,
      totalSpent: totalSpent,
      years: years,
      firstVisit: confirmedRes.length > 0 ? confirmedRes[confirmedRes.length - 1].checkIn : null,
      lastVisit: confirmedRes.length > 0 ? confirmedRes[0].checkIn : null,
      source: 'sirvoy'
    };
    
  } catch (error) {
    console.error('Erreur calcul stats client Sirvoy:', error);
    return {
      totalReservations: 0,
      totalCancellations: 0,
      totalSpent: 0,
      years: [],
      firstVisit: null,
      lastVisit: null,
      source: 'sirvoy'
    };
  }
}

/**
 * Vérifie si les archives Sirvoy sont disponibles (connexion OK)
 * 
 * @returns {Promise<boolean>}
 */
export async function checkSirvoyConnection() {
  try {
    const bookingsRef = collection(sirvoyDb, 'bookings');
    const snapshot = await getDocs(query(bookingsRef));
    console.log(`✅ Connexion Sirvoy OK - ${snapshot.size} réservations en base`);
    return true;
  } catch (error) {
    console.error('❌ Connexion Sirvoy échouée:', error);
    return false;
  }
}
